import { c as _c } from "react/compiler-runtime";
// The Settings view (/settings, from the session row), a path of pages (router.tsx): Settings \u203a Sessions |
// Instances \u203a Connect | one instance \u203a Members | Tokens | its Ontologies. A row opens its page after this
// one (usePage: the next path segment); tapping it again closes it. Sessions, Connect, Members and Tokens are
// modals (?sessions=1 …, layouts/modals.tsx: the Sheet on phones).
// SettingsPage: who you are (name, kind), Sessions, Instances, theme, logout. (Secrets are per project: a nav view.)
// InstancesPage: this home + every instance connected to it (Connect grants, either way), then what the kind views
// add (src/views: the managed instances); an instance's page gets the views' rows too.
// InstancePage: this home — Members, Tokens, its Ontologies; a connected one — its
// grants (Disconnect), its Ontologies read-only, a link
// to its own web.
// Built from the shared pieces only: BookPage + PageHead, Group, SectionHead, Segmented, Initials.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { api, type Connection, type Session } from "@/api";
import { useLoad } from "@/lib/load";
import { useFlag, useParam, usePage } from "@/lib/hooks";
import { useViews } from "@/views";
import { ConnectForm, Grants } from "@/pages/connections";
import { IconButton } from "./icon-button";
import { Icon } from "./icon";
import { LinkButton } from "./link-button";
import { useAuth, useData } from "@/store";
import { THEMES, useTheme } from "@/store/theme";
import { Group, GroupRow, Item } from "./group";
import { Initials } from "./initials";
import { BookPage, PageHead } from "./page";
import { SectionHead } from "./section-head";
import { Segment, Segmented } from "./segmented";

/** This home's name: the session's, else the one chosen at login. */
export function useHome() {
  const session = useSession();
  const instance = useAuth(_temp);
  return session?.instance || instance || "";
}

/** One session request per sign-in, shared by every page that asks (it was one per component). */
function _temp(s) {
  return s.instance;
}
let sessionP: {
  token: string | null;
  p: Promise<Session>;
} | null = null;
const sessionOnce = () => {
  const token = useAuth.getState().token;
  if (!sessionP || sessionP.token !== token) {
    const p = api.session.get?.() ?? Promise.reject(new Error("no session"));
    sessionP = {
      token,
      p
    };
    p.catch(() => sessionP?.p === p && (sessionP = null));
  }
  return sessionP.p;
};
export function useSession() {
  const $ = _c(2);
  const [session, setSession] = useState(null);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      let live = true;
      sessionOnce().then(s => live && setSession(s), _temp2);
      return () => {
        live = false;
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return session;
}
function _temp2() {}
export function SettingsPage() {
  const $ = _c(64);
  const {
    t
  } = useTranslation();
  const {
    me,
    signOut
  } = useAuth();
  const page = usePage();
  const sessions = useFlag("sessions");
  const {
    theme,
    setTheme
  } = useTheme();
  const session = useSession();
  const person = session?.person ?? me;
  let t0;
  if ($[0] !== person || $[1] !== t) {
    t0 = person ? t(`settings.${person.kind}`) : "—";
    $[0] = person;
    $[1] = t;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const kind = t0;
  let t1;
  if ($[3] !== t) {
    t1 = t("settings.title");
    $[3] = t;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t) {
    t2 = t("settings.title");
    $[5] = t;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t2) {
    t3 = <PageHead title={t2} />;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = person?.name ?? "?";
  let t5;
  if ($[9] !== t4) {
    t5 = <Initials name={t4} />;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = person?.name ?? "…";
  let t7;
  if ($[11] !== t6) {
    t7 = <div className="name">{t6}</div>;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = person ? kind : "";
  let t9;
  if ($[13] !== t8) {
    t9 = <div className="muted">{t8}</div>;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t7 || $[16] !== t9) {
    t10 = <div>{t7}{t9}</div>;
    $[15] = t7;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t5) {
    t11 = <div className="who">{t5}{t10}</div>;
    $[18] = t10;
    $[19] = t5;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t) {
    t12 = t("sessions.title");
    $[21] = t;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== sessions.on || $[24] !== sessions.toggle || $[25] !== t12) {
    t13 = <GroupRow t="sessions-row" label={t12} chevron={true} aria-expanded={sessions.on} onClick={sessions.toggle} />;
    $[23] = sessions.on;
    $[24] = sessions.toggle;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t) {
    t14 = t("settings.instances");
    $[27] = t;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const t15 = page.open === "instances";
  let t16;
  if ($[29] !== page) {
    t16 = () => page.toggle("instances");
    $[29] = page;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t14 || $[32] !== t15 || $[33] !== t16) {
    t17 = <GroupRow t="instances-row" label={t14} chevron={true} aria-expanded={t15} onClick={t16} />;
    $[31] = t14;
    $[32] = t15;
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t13 || $[36] !== t17) {
    t18 = <Group>{t13}{t17}</Group>;
    $[35] = t13;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== t) {
    t19 = t("settings.theme");
    $[38] = t;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== t19) {
    t20 = <SectionHead>{t19}</SectionHead>;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== t) {
    t21 = t("settings.theme");
    $[42] = t;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== setTheme || $[45] !== t || $[46] !== theme) {
    t22 = THEMES.map(x => <Segment key={x} pressed={theme === x} onClick={() => setTheme(x)}>{t(`settings.${x}`)}</Segment>);
    $[44] = setTheme;
    $[45] = t;
    $[46] = theme;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t21 || $[49] !== t22) {
    t23 = <Segmented t="theme" label={t21}>{t22}</Segmented>;
    $[48] = t21;
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t) {
    t24 = t("nav.signOut");
    $[51] = t;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== signOut || $[54] !== t24) {
    t25 = <div className="sign-out"><LinkButton data-t="sign-out" className="danger" onClick={signOut}>{t24}</LinkButton></div>;
    $[53] = signOut;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t1 || $[57] !== t11 || $[58] !== t18 || $[59] !== t20 || $[60] !== t23 || $[61] !== t25 || $[62] !== t3) {
    t26 = <BookPage t="settings" page="settings" label={t1} className="settings">{t3}{t11}{t18}{t20}{t23}{t25}</BookPage>;
    $[56] = t1;
    $[57] = t11;
    $[58] = t18;
    $[59] = t20;
    $[60] = t23;
    $[61] = t25;
    $[62] = t3;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  return t26;
}

/** Connected instances: name \u2192 its grants (outgoing, incoming or both). */
function useConnected() {
  const $ = _c(8);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      kinds: ["Grant", "Connection"]
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const list = useLoad(_temp3, "connections", t0);
  let by;
  if ($[1] !== list.data) {
    by = new Map();
    for (const c of list.data ?? []) {
      if (c.status !== "revoked") {
        by.set(c.instance, [...(by.get(c.instance) ?? []), c]);
      }
    }
    $[1] = list.data;
    $[2] = by;
  } else {
    by = $[2];
  }
  const t1 = !!list.data;
  let t2;
  if ($[3] !== by || $[4] !== list.loading || $[5] !== list.reload || $[6] !== t1) {
    t2 = {
      by,
      reload: list.reload,
      loaded: t1,
      loading: list.loading
    };
    $[3] = by;
    $[4] = list.loading;
    $[5] = list.reload;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function _temp3() {
  return api.connections.list();
}
export function InstancesPage() {
  const $ = _c(67);
  const {
    t
  } = useTranslation();
  const home = useHome();
  const connected = useConnected();
  const navigate = useNavigate();
  const page = usePage();
  const selected = page.open;
  let t0;
  if ($[0] !== page) {
    t0 = name => page.toggle(name);
    $[0] = page;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const go = t0;
  const connect = useParam("connect");
  const views = useViews();
  const [back] = useState(_temp4);
  let t1;
  if ($[2] !== back || $[3] !== navigate) {
    t1 = () => {
      if (back) {
        navigate({
          search: ""
        }, {
          replace: true
        });
      }
    };
    $[2] = back;
    $[3] = navigate;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== back) {
    t2 = [back];
    $[5] = back;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[7] !== back || $[8] !== connected.loaded) {
    t3 = () => {
      if (back && connected.loaded) {
        useData.getState().load();
      }
    };
    t4 = [back, connected.loaded];
    $[7] = back;
    $[8] = connected.loaded;
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  useEffect(t3, t4);
  let t5;
  if ($[11] !== t) {
    t5 = t("settings.instances");
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t) {
    t6 = t("settings.instances");
    $[13] = t;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t) {
    t7 = t("connections.connectButton");
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = !!connect.value;
  let t9;
  if ($[17] !== connect) {
    t9 = () => connect.value ? connect.close() : connect.open("1");
    $[17] = connect;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Icon name="link" size={18} />;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t7 || $[21] !== t8 || $[22] !== t9) {
    t11 = <IconButton t="connect-open" label={t7} aria-expanded={t8} onClick={t9}>{t10}</IconButton>;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11 || $[25] !== t6) {
    t12 = <PageHead title={t6} actions={t11} />;
    $[24] = t11;
    $[25] = t6;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== back || $[28] !== t) {
    t13 = back && <div className="note" data-t="connected">{t("connections.connected", {
        instance: back
      })}</div>;
    $[27] = back;
    $[28] = t;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const t14 = connected.loading;
  const t15 = home || "?";
  let t16;
  if ($[30] !== t15) {
    t16 = <Initials name={t15} />;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  const t17 = home || "…";
  let t18;
  if ($[32] !== t) {
    t18 = t("settings.home");
    $[32] = t;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  const t19 = selected === home;
  let t20;
  if ($[34] !== go || $[35] !== home) {
    t20 = () => go(home);
    $[34] = go;
    $[35] = home;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== home || $[38] !== t16 || $[39] !== t17 || $[40] !== t18 || $[41] !== t19 || $[42] !== t20) {
    t21 = <GroupRow t="instance-row" id={home} lead={t16} label={t17} count={t18} chevron={true} aria-expanded={t19} onClick={t20} />;
    $[37] = home;
    $[38] = t16;
    $[39] = t17;
    $[40] = t18;
    $[41] = t19;
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== connected.by) {
    t22 = [...connected.by.keys()];
    $[44] = connected.by;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== go || $[47] !== selected || $[48] !== t22) {
    t23 = t22.map(name_0 => <GroupRow key={name_0} t="instance-row" id={name_0} lead={<Initials name={name_0} />} label={name_0} chevron={true} aria-expanded={selected === name_0} onClick={() => go(name_0)} />);
    $[46] = go;
    $[47] = selected;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== connected.loading || $[51] !== t21 || $[52] !== t23) {
    t24 = <Group t="instances" busy={t14}>{t21}{t23}</Group>;
    $[50] = connected.loading;
    $[51] = t21;
    $[52] = t23;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== go || $[55] !== selected || $[56] !== views) {
    let t26;
    if ($[58] !== go || $[59] !== selected) {
      t26 = (v, i) => v.Instances && <v.Instances key={i} open={selected} onOpen={go} />;
      $[58] = go;
      $[59] = selected;
      $[60] = t26;
    } else {
      t26 = $[60];
    }
    t25 = views.map(t26);
    $[54] = go;
    $[55] = selected;
    $[56] = views;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[61] !== t12 || $[62] !== t13 || $[63] !== t24 || $[64] !== t25 || $[65] !== t5) {
    t26 = <BookPage t="instances" page="instances" label={t5} className="settings">{t12}{t13}{t24}{t25}</BookPage>;
    $[61] = t12;
    $[62] = t13;
    $[63] = t24;
    $[64] = t25;
    $[65] = t5;
    $[66] = t26;
  } else {
    t26 = $[66];
  }
  return t26;
}

/** The Connect panel's content (opened by the Instances page's +, or an invite's ?connect=<origin>). */
function _temp4() {
  return new URLSearchParams(location.search).get("connected");
}
export function ConnectPanel(t0) {
  const $ = _c(6);
  const {
    value,
    onClose
  } = t0;
  const connected = useConnected();
  let t1;
  if ($[0] !== connected || $[1] !== onClose) {
    t1 = () => {
      connected.reload();
      onClose?.();
    };
    $[0] = connected;
    $[1] = onClose;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1 || $[4] !== value) {
    t2 = <ConnectForm initial={value} onDone={t1} />;
    $[3] = t1;
    $[4] = value;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
export function InstancePage(t0) {
  const $ = _c(78);
  const {
    name
  } = t0;
  const {
    t
  } = useTranslation();
  const home = useHome();
  const connected = useConnected();
  const page = usePage();
  let t1;
  if ($[0] !== page) {
    t1 = v => page.toggle(v);
    $[0] = page;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const go = t1;
  const members = useFlag("members");
  const tokens = useFlag("tokens");
  const views = useViews();
  let t2;
  if ($[2] !== name || $[3] !== views) {
    let t3;
    if ($[5] !== name) {
      t3 = (v_0, i) => v_0.Instance && <v_0.Instance key={i} name={name} />;
      $[5] = name;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t2 = views.map(t3);
    $[2] = name;
    $[3] = views;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const records = t2;
  if (name !== home) {
    let t3;
    if ($[7] !== connected.by || $[8] !== name) {
      t3 = connected.by.get(name) ?? [];
      $[7] = connected.by;
      $[8] = name;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    const cs = t3;
    const url = cs[0]?.url ?? "";
    const t4 = connected.loading;
    let t5;
    if ($[10] !== t) {
      t5 = t("settings.instance");
      $[10] = t;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    let t6;
    if ($[12] !== name) {
      t6 = <span className="mono">{name}</span>;
      $[12] = name;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    let t7;
    if ($[14] !== t5 || $[15] !== t6) {
      t7 = <PageHead meta={t5} title={t6} />;
      $[14] = t5;
      $[15] = t6;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    const t8 = url || "—";
    let t9;
    if ($[17] !== t8) {
      t9 = <Group><Item label="URL" value={t8} mono={true} /></Group>;
      $[17] = t8;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    let t10;
    if ($[19] !== connected.reload || $[20] !== cs) {
      t10 = <Grants grants={cs} onChange={connected.reload} />;
      $[19] = connected.reload;
      $[20] = cs;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    let t11;
    if ($[22] !== t) {
      t11 = t("nav.ontologies");
      $[22] = t;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    const t12 = page.open === "ontologies";
    let t13;
    if ($[24] !== go) {
      t13 = () => go("ontologies");
      $[24] = go;
      $[25] = t13;
    } else {
      t13 = $[25];
    }
    let t14;
    if ($[26] !== t11 || $[27] !== t12 || $[28] !== t13) {
      t14 = <Group><GroupRow t="ontologies-row" label={t11} chevron={true} aria-expanded={t12} onClick={t13} /></Group>;
      $[26] = t11;
      $[27] = t12;
      $[28] = t13;
      $[29] = t14;
    } else {
      t14 = $[29];
    }
    let t15;
    if ($[30] !== t || $[31] !== url) {
      t15 = url && <Group><GroupRow t="open-instance" label={t("settings.open")} chevron={true} onClick={() => window.open(url, "_blank", "noopener")} /></Group>;
      $[30] = t;
      $[31] = url;
      $[32] = t15;
    } else {
      t15 = $[32];
    }
    let t16;
    if ($[33] !== connected.loading || $[34] !== name || $[35] !== records || $[36] !== t10 || $[37] !== t14 || $[38] !== t15 || $[39] !== t7 || $[40] !== t9) {
      t16 = <BookPage t="instance" page="instance" id={name} label={name} className="settings" busy={t4}>{t7}{t9}{t10}{records}{t14}{t15}</BookPage>;
      $[33] = connected.loading;
      $[34] = name;
      $[35] = records;
      $[36] = t10;
      $[37] = t14;
      $[38] = t15;
      $[39] = t7;
      $[40] = t9;
      $[41] = t16;
    } else {
      t16 = $[41];
    }
    return t16;
  }
  let t3;
  if ($[42] !== t) {
    t3 = t("settings.home");
    $[42] = t;
    $[43] = t3;
  } else {
    t3 = $[43];
  }
  let t4;
  if ($[44] !== name) {
    t4 = <span className="mono">{name}</span>;
    $[44] = name;
    $[45] = t4;
  } else {
    t4 = $[45];
  }
  let t5;
  if ($[46] !== t3 || $[47] !== t4) {
    t5 = <PageHead meta={t3} title={t4} />;
    $[46] = t3;
    $[47] = t4;
    $[48] = t5;
  } else {
    t5 = $[48];
  }
  let t6;
  if ($[49] !== t) {
    t6 = t("settings.members");
    $[49] = t;
    $[50] = t6;
  } else {
    t6 = $[50];
  }
  let t7;
  if ($[51] !== members.on || $[52] !== members.toggle || $[53] !== t6) {
    t7 = <GroupRow t="members-row" label={t6} chevron={true} aria-expanded={members.on} onClick={members.toggle} />;
    $[51] = members.on;
    $[52] = members.toggle;
    $[53] = t6;
    $[54] = t7;
  } else {
    t7 = $[54];
  }
  let t8;
  if ($[55] !== t) {
    t8 = t("settings.tokens");
    $[55] = t;
    $[56] = t8;
  } else {
    t8 = $[56];
  }
  let t9;
  if ($[57] !== t8 || $[58] !== tokens.on || $[59] !== tokens.toggle) {
    t9 = <GroupRow t="tokens-row" label={t8} chevron={true} aria-expanded={tokens.on} onClick={tokens.toggle} />;
    $[57] = t8;
    $[58] = tokens.on;
    $[59] = tokens.toggle;
    $[60] = t9;
  } else {
    t9 = $[60];
  }
  let t10;
  if ($[61] !== t7 || $[62] !== t9) {
    t10 = <Group>{t7}{t9}</Group>;
    $[61] = t7;
    $[62] = t9;
    $[63] = t10;
  } else {
    t10 = $[63];
  }
  let t11;
  if ($[64] !== t) {
    t11 = t("nav.ontologies");
    $[64] = t;
    $[65] = t11;
  } else {
    t11 = $[65];
  }
  const t12 = page.open === "ontologies";
  let t13;
  if ($[66] !== go) {
    t13 = () => go("ontologies");
    $[66] = go;
    $[67] = t13;
  } else {
    t13 = $[67];
  }
  let t14;
  if ($[68] !== t11 || $[69] !== t12 || $[70] !== t13) {
    t14 = <Group><GroupRow t="ontologies-row" label={t11} chevron={true} aria-expanded={t12} onClick={t13} /></Group>;
    $[68] = t11;
    $[69] = t12;
    $[70] = t13;
    $[71] = t14;
  } else {
    t14 = $[71];
  }
  let t15;
  if ($[72] !== name || $[73] !== records || $[74] !== t10 || $[75] !== t14 || $[76] !== t5) {
    t15 = <BookPage t="instance" page="instance" id={name} label={name} className="settings">{t5}{t10}{records}{t14}</BookPage>;
    $[72] = name;
    $[73] = records;
    $[74] = t10;
    $[75] = t14;
    $[76] = t5;
    $[77] = t15;
  } else {
    t15 = $[77];
  }
  return t15;
}